<template>
  <div
    v-if="modelValue"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
  >
    <div class="bg-white w-full max-w-lg rounded-xl shadow-lg p-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-800">
          {{ isEdit ? "Edit Event" : "Create Event" }}
        </h2>
        <button
          @click="close"
          class="text-gray-400 hover:text-gray-600 text-xl"
        >
          &times;
        </button>
      </div>
      <form @submit.prevent="submit" class="space-y-4">
        <div>
          <label class="text-sm text-gray-700">Event Name</label>
          <input
            v-model="form.eventName"
            type="text"
            class="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label class="text-sm text-gray-700">Description</label>
          <textarea
            v-model="form.description"
            rows="3"
            placeholder="Enter event description (optional)"
            class="w-full mt-1 border rounded-lg px-3 py-2 text-sm resize-none"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm text-gray-700">Date From</label>
            <input
              v-model="form.dateFrom"
              type="date"
              class="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label class="text-sm text-gray-700">Date To</label>
            <input
              v-model="form.dateTo"
              type="date"
              class="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
        </div>
        <div>
          <label class="text-sm text-gray-700">Time</label>
          <input
            v-model="form.time"
            type="time"
            class="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label class="text-sm text-gray-700">Venue</label>
          <div class="w-full mt-1">
            <GMapAutocomplete
              ref="gmapAutocompleteRef"
              :options="{
                componentRestrictions: { country: 'PH' },
                types: ['establishment'],
              }"
              @place_changed="setPlace"
              @keypress.enter.prevent
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 pt-4">
          <button
            type="button"
            @click="close"
            class="px-4 py-2 text-sm rounded-lg border text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { reactive, computed, watch, inject, ref } from "vue";

export default {
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    eventData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue", "submit"],
  setup(props, { emit }) {
    const Swal = inject("$swal");
    const axios = inject("$axios");

    const gmapAutocompleteRef = ref(null);

    const form = reactive({
      sectionId: 3,
      eventName: "",
      description: "",
      dateFrom: "",
      dateTo: "",
      time: "",
      venue: "",
    });

    const isEdit = computed(() => {
      return props.eventData && Object.keys(props.eventData).length > 0;
    });

    watch(
      () => props.eventData,
      (val) => {
        if (val && Object.keys(val).length) {
          form.eventName = val.event_name || "";
          form.description = val.description || "";
          form.dateFrom = val.date_intended_from || "";
          form.dateTo = val.date_intended_to || "";
          form.time = val.time_intended || "";
          form.venue = val.venue || "";
        }
      },
      { immediate: true }
    );

    const setPlace = (place) => {
      if (!place) return;
      form.venue = place.name;
      if (gmapAutocompleteRef.value?.$el) {
        gmapAutocompleteRef.value.$el.value = place.name;
      }
    };

    const resetForm = () => {
      form.eventName = "";
      form.description = "";
      form.dateFrom = "";
      form.dateTo = "";
      form.time = "";
      form.venue = "";

      if (gmapAutocompleteRef.value?.$el) {
        gmapAutocompleteRef.value.$el.value = "";
      }
    };

    const close = () => {
      emit("update:modelValue", false);
    };

    const submit = async () => {
      try {
        const payload = {
          section_id: form.sectionId,
          event_name: form.eventName,
          description: form.description,
          date_intended_from: form.dateFrom,
          date_intended_to: form.dateTo,
          time_intended: form.time,
          venue: form.venue,
        };

        const response = await axios.post(
          "/api/module-post-calendar-events",
          payload
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          confirmButtonColor: "#f97316",
        });

        emit("submit", response.data.data);
        resetForm();
        close();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text:
            error.response?.data?.message ||
            "Something went wrong while saving the event.",
        });
      }
    };

    return {
      form,
      isEdit,
      setPlace,
      close,
      submit,
      resetForm,
      gmapAutocompleteRef,
    };
  },
};
</script>

<style scoped>
:deep(.pac-target-input) {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

:deep(.pac-target-input:focus) {
  outline: none;
  border-color: #f97316;
}
</style>
